import { memo, useCallback, useContext, useEffect, useRef, useState } from "react";
import { isEqual } from "lodash-es";
// context
import { DispatchContext } from "bodycodi/cmap/app/components/common/context";

// components
import PopupHeader from "bodycodi/cmap/app/components/common/popupHeader";
import ImagePickerDrawer from "../../imgPickDrawer";
import SNSLinks from "./SNSLinks";
import ImageUploadAlert from "../../../common/ImageUploadAlert";
import ShareAlert from "bodycodi/cmap/app/components/common/shareAlert";

// assets
import CoachDefault from "bodycodi/cmap/app/svg/bodycodi_plus_default_coach_profile.svg";
import CameraIcon from "bodycodi/cmap/app/svg/camera_icon.svg";

// utils
import { getSnsCode, isEmpty, openCommonAlert } from "bodycodi/cmap/app/js/common";
import { api } from "bodycodi/cmap/app/js/api";

import { BODYDCODI_PLUS_URL } from "bodycodi/cmap/app/data/bodycodi-plus";
import RequestUploadAlert from "../../../common/RequestUploadAlert";

const BODYCODI_PLUS_SELLER_PROFILE_SHARE_DEFAULT_DESCRIPTION = "당신의 건강한 일상을 함께할게요.";

const BODYCODI_PLUS_SELLER_PROGRAM_SHARE_DEFAULT_DESCRIPTION =
  "맞춤 헬스 프로그램으로 당신의 건강한 변화를 지금 시작해보세요!";

const BODYCODI_PLUS_SHARE_DEFAULT_IMAGE_URL_KAKAO =
  "https://static.bodycodi.com/plus/common/kakao_share_800x800.jpg";

const SellerProfileForm = ({ seqPlusSeller, seqPartnerCoach, coachName }: any) => {
  const profilePicRef = useRef<any>(null);
  const { dispatch } = useContext(DispatchContext);

  const [imageUploadAlert, setImageUploadAlert] = useState<any>({
    open: false,
    header: "",
    body: "",
    onChange: () => {},
  });
  const [requestUploadAlert, setRequestUploadAlert] = useState<any>({
    open: false,
    header: "",
    body: "",
    buttonLabel: "",
    onClick: () => {},
    onCancel: () => {},
  });
  const [originalDetails, setOriginalDetails] = useState<any>("");
  const [coverImage, setCoverImage] = useState<any>("");
  const [profilePic, setProfilePic] = useState<any>("");
  const [shortIntro, setShortIntro] = useState<string>("");
  const [detailTitle, setDetailTitle] = useState<string>("");
  const [careerDesc, setCareerDesc] = useState<string>("");
  const [detailImage, setDetailImage] = useState<any>("");
  const [snsLinks, setSnsLinks] = useState<any>({
    displayLinks: [],
    deletedLinks: [],
  });

  const [shareOpen, setShareOpen] = useState(false);
  const [currentTab, setCurrentTab] = useState("coach");
  const [encryptSeqPlusSeller, setEncryptSeqPlusSeller] = useState("");
  const handleTab = (tab: string) => () => setCurrentTab(tab);

  const cryptoShareLink = async () => {
    try {
      dispatch({ type: "LOADING_START" });
      await api.get(`plus/sellers/link/${seqPlusSeller}`).then(({ data }) => {
        setEncryptSeqPlusSeller(data?.seqPlusSeller);
        dispatch({ type: "LOADING_END" });
      });
    } catch (error) {
      dispatch({ type: "LOADING_END" });
      console.error(error);
    }
  };

  const handleShare = async () => {
    const oDetails = { ...originalDetails };
    delete oDetails?.name;
    delete oDetails?.seqPlusSeller;

    const currentReq = {
      coverImgUrl: coverImage,
      profileImgUrl: profilePic,
      detailImgUrl: detailImage,
      plusSellerLinks: {
        displayLinks: snsLinks?.displayLinks?.filter((item: any) => item?.snsLink !== ""),
        deletedLinks: [],
      },
      shortIntro,
      detailTitle,
      careerDesc,
    };

    if (isEqual(oDetails, currentReq)) {
      await cryptoShareLink();
      setShareOpen(true);
    } else {
      setRequestUploadAlert({
        open: true,
        header: "프로필 수정 완료하기",
        body: `프로필을 수정한 내용이 있어요.\n저장할까요?`,
        buttonLabel: "저장하기",
        onClick: async () => {
          await editSellerProfile(false)
            .then(() => {
              setRequestUploadAlert((prev: any) => ({ ...prev, open: false }));
            })
            .then(async () => {
              await cryptoShareLink();
              setTimeout(() => {
                setShareOpen(true);
              }, 500);
            });
        },
        onCancel: async () => {
          setRequestUploadAlert((prev: any) => ({ ...prev, open: false }));
          setTimeout(() => {
            setShareOpen(true);
          }, 500);
        },
      });
    }
  };

  const handleSubmit = () => {
    if (!!seqPlusSeller) editSellerProfile();
    else createSellerProfile();
  };

  const createSellerProfile = async () => {
    try {
      dispatch({ type: "LOADING_START" });
      let reqBody: any = {
        coverImgUrl: null,
        profileImgUrl: null,
        detailImgUrl: null,
        sellerLinkRequests: [],
      };

      if (coverImage) reqBody["coverImgUrl"] = await uploadImageToServer(coverImage);
      if (profilePic) reqBody["profileImgUrl"] = await uploadImageToServer(profilePic, 400);
      if (detailImage) reqBody["detailImgUrl"] = await uploadImageToServer(detailImage);
      if (snsLinks.length) {
        reqBody["sellerLinkRequests"] = snsLinks
          .map((item: any) => {
            if (!!item.snsLink.length)
              return {
                snsLink: item.snsLink,
                snsCode: getSnsCode(item.snsLink),
              };
          })
          .filter((item: any) => !!item);
      }

      await api
        .post("/plus/sellers/create", {
          name: coachName,
          shortIntro,
          detailTitle,
          careerDesc,
          ...reqBody,
        })
        .then(({ data }) => {
          dispatch({ type: "LOADING_END" });
          dispatch({ type: "UPDATE", data: { toast: true, toastText: "저장하였습니다." } });
        });
    } catch (error) {
      dispatch({ type: "LOADING_END" });
      console.error(error);
    }
  };

  const editSellerProfile = async (showToast = true) => {
    try {
      dispatch({ type: "LOADING_START" });

      let reqBody: any = {
        coverImgUrl: coverImage,
        profileImgUrl: profilePic,
        detailImgUrl: detailImage,
        updateSellerLinks: snsLinks,
      };

      // if cover image is changed
      if (await shouldUploadImage(null, coverImage)) {
        reqBody["coverImgUrl"] = await uploadImageToServer(coverImage);
      }

      // if profile image is changed
      if (await shouldUploadImage(null, profilePic)) {
        reqBody["profileImgUrl"] = await uploadImageToServer(profilePic, 400);
      }

      // if detail image is changed
      if (await shouldUploadImage(null, detailImage)) {
        reqBody["detailImgUrl"] = await uploadImageToServer(detailImage);
      }

      // check sns links
      reqBody["updateSellerLinks"] = await checkSnsLinks();

      await api
        .post("/plus/sellers/update", {
          seqPlusSeller,
          name: coachName,
          shortIntro,
          detailTitle,
          careerDesc,
          ...reqBody,
        })
        .then(({ data }) => {
          dispatch({ type: "LOADING_END" });
          if (showToast) {
            dispatch({
              type: "UPDATE",
              data: { toast: true, toastText: "저장하였습니다." },
            });
          }
          fetchSellerDetails();
        });
    } catch (error) {
      dispatch({ type: "LOADING_END" });
      console.error(error);
    }
  };

  const shouldUploadImage = async (originalUrl: any, newImage: any) => {
    return (
      (isEmpty(originalUrl) && typeof newImage === "object") || // no image was set before
      (!!originalUrl && typeof originalUrl === "string" && typeof newImage === "object") // existing image has been changed to new image
    );
  };

  const uploadImageToServer = async (image: File, width = 720) => {
    try {
      const formData: any = new FormData();
      formData.append("imageFile", image);
      formData.append("imageWidth", width);
      formData.append("imageUploadDomain", "plus/sellers");

      return await api
        .post("/upload/images", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        .then(({ data }) => {
          return data.imageUrlPath;
        });
    } catch (error: any) {
      console.error(error.message);
    }
  };

  const checkSnsLinks = async () => {
    const deleted = snsLinks.deletedLinks;
    const updated = snsLinks.displayLinks
      .map((item: any) => {
        if (item?.useYn === "Y" && !!item?.snsLink.length) {
          return {
            ...item,
            snsCode: getSnsCode(item.snsLink),
          };
        }
      })
      .filter((item: any) => !!item && item.snsLink !== "");

    const newlyAdded = snsLinks.displayLinks
      .map((item: any) => {
        if (
          typeof item.seqPlusSellerLink === "string" &&
          item.seqPlusSellerLink.startsWith("new+") &&
          !!item?.snsLink.length
        ) {
          return {
            snsLink: item.snsLink,
            snsCode: getSnsCode(item.snsLink),
          };
        }
      })
      .filter((item: any) => !!item);

    return [...deleted, ...updated, ...newlyAdded];
  };

  const openImageUploadAlert = useCallback((type: "detail" | "cover") => {
    if (type === "cover") {
      setImageUploadAlert({
        open: true,
        header: "강사 프로필 이미지 설정",
        body: `업로드 사이즈는 4:3 비율(750픽셀 X 560픽셀)이며, \n 업로드 최대 파일의 크기는 5MB입니다.`,
        onChange: (e: any) => onChangeHandler(e, "cover"),
      });
    } else {
      setImageUploadAlert({
        open: true,
        header: "강사 상세 이미지 설정",
        body: `업로드 사이즈는 4:3 비율(670픽셀 X 560픽셀)이며, \n 업로드 최대 파일의 크기는 1MB입니다.`,
        onChange: (e: any) => onChangeHandler(e, "detail"),
      });
    }
  }, []);

  const closeImageUploadAlert = useCallback(() => {
    setImageUploadAlert({
      ...imageUploadAlert,
      open: false,
    });
  }, []);

  const selectProfilePic = useCallback(() => {
    const file = profilePicRef.current!.files[0];
    setProfilePic(file);
    closeImagePickerDrawer();
  }, []);

  const closeImagePickerDrawer = () => {
    dispatch({
      type: "POP_UP_OPEN",
      id: "IMG_PICKER_DRAWER_POP",
      open: { imgPickerDrawerPop: false },
    });
  };

  const onChangeHandler = useCallback((e: any, type: "detail" | "cover") => {
    if (e.target.files[0].size === 0) return;

    const file = e.target.files[0];
    const acceptable = type === "cover" ? file.size < 5 * 1024 * 1024 : file.size < 1 * 1024 * 1024;

    if (acceptable) {
      type === "cover" ? setCoverImage(file) : setDetailImage(file);
    } else {
      openCommonAlert({
        header: "이미지 설정",
        body:
          type === "cover"
            ? "5MB이상 사진은 업로드 할 수 없습니다."
            : "1MB이상 사진은 업로드 할 수 없습니다.",
      });
    }

    e.target.value = null;
    closeImageUploadAlert();
  }, []);

  const fetchSellerDetails = async () => {
    try {
      dispatch({ type: "LOADING_START" });
      await api
        .post(`/plus/sellers/detail`, {
          seqPartnerCoach,
          seqPlusSeller,
        })
        .then(({ data }) => {
          setOriginalDetails({
            ...data,
            plusSellerLinks: {
              displayLinks: data?.plusSellerLinks?.length ? data?.plusSellerLinks : [],
              deletedLinks: [],
            },
          });
          setCoverImage(data.coverImgUrl || "");
          setProfilePic(data.profileImgUrl || "");
          setShortIntro(data.shortIntro || "");
          setDetailTitle(data.detailTitle || "");
          setCareerDesc(data.careerDesc || "");
          setDetailImage(data.detailImgUrl || "");
          setSnsLinks({ ...snsLinks, displayLinks: data.plusSellerLinks || [] });
          dispatch({ type: "LOADING_END" });
        });
    } catch (error: any) {
      dispatch({ type: "LOADING_END" });
      console.error(error.message);
    }
  };

  const handleClose = () => {
    const oDetails = { ...originalDetails };
    delete oDetails?.name;
    delete oDetails?.seqPlusSeller;

    const currentReq = {
      coverImgUrl: coverImage,
      profileImgUrl: profilePic,
      detailImgUrl: detailImage,
      plusSellerLinks: {
        displayLinks: snsLinks?.displayLinks?.filter((item: any) => item?.snsLink !== ""),
        deletedLinks: [],
      },
      shortIntro,
      detailTitle,
      careerDesc,
    };

    if (isEqual(oDetails, currentReq)) {
      dispatch({
        type: "POP_UP_OPEN",
        id: "SELLEER_PROFILE_POP",
        open: { sellerProfilePop: false },
      });
    } else {
      setRequestUploadAlert({
        open: true,
        header: "프로필 수정 완료하기",
        body: `프로필을 수정한 내용이 있어요.\n저장할까요?`,
        buttonLabel: "저장하기",
        onClick: async () => {
          await editSellerProfile(false).then(() => {
            setRequestUploadAlert((prev: any) => ({ ...prev, open: false }));
            setTimeout(() => {
              dispatch({
                type: "POP_UP_OPEN",
                id: "SELLEER_PROFILE_POP",
                open: { sellerProfilePop: false },
              });
            }, 500);
          });
        },
        onCancel: () => {
          setRequestUploadAlert((prev: any) => ({ ...prev, open: false }));
          setTimeout(() => {
            dispatch({
              type: "POP_UP_OPEN",
              id: "SELLEER_PROFILE_POP",
              open: { sellerProfilePop: false },
            });
          }, 250);
        },
      });
    }
  };

  useEffect(() => {
    fetchSellerDetails();
  }, []);

  return (
    <>
      <PopupHeader title="셀러 프로필 관리" onClickBack={handleClose} />
      <div className="popup-body no-padding">
        <div className="seller-profile">
          <CoverImage coverImage={coverImage} openImageUploadAlert={openImageUploadAlert} />

          <Overview
            name={coachName}
            profilePic={profilePic}
            setProfilePic={setProfilePic}
            shortIntro={shortIntro}
            setShortIntro={setShortIntro}
          />

          <Greeting
            detailTitle={detailTitle}
            setDetailTitle={setDetailTitle}
            careerDesc={careerDesc}
            setCareerDesc={setCareerDesc}
          />

          <DetailImage detailImage={detailImage} openImageUploadAlert={openImageUploadAlert} />

          <SNSLinks snsLinks={snsLinks} setSnsLinks={setSnsLinks} />

          <div className="confirm-button">
            <button onClick={handleShare}>공유하기</button>
            <button onClick={handleSubmit}>완료</button>
          </div>
        </div>

        <ShareAlert
          type="tab"
          activeTab={currentTab}
          tabs={{
            coach: "강사 프로필 공유",
            program: "프로그램 공유",
          }}
          program={{
            title:
              currentTab === "program"
                ? `${coachName}의 추천 프로그램 | bodycodi+`
                : `${coachName}의 프로필 | bodycodi+`,
            description:
              currentTab === "program"
                ? BODYCODI_PLUS_SELLER_PROGRAM_SHARE_DEFAULT_DESCRIPTION
                : detailTitle && detailTitle !== ""
                ? detailTitle
                : BODYCODI_PLUS_SELLER_PROFILE_SHARE_DEFAULT_DESCRIPTION,
            mainCoverImgUrl:
              currentTab === "program"
                ? BODYCODI_PLUS_SHARE_DEFAULT_IMAGE_URL_KAKAO
                : coverImage ?? BODYCODI_PLUS_SHARE_DEFAULT_IMAGE_URL_KAKAO,
          }}
          link={`${BODYDCODI_PLUS_URL}/coach/${encryptSeqPlusSeller}${
            currentTab === "program" ? "?target=program" : ""
          }`}
          description={
            currentTab === "coach"
              ? `강사 프로필을 공유해서 홍보해 보세요.\n해당 링크로 바로 확인할 수 있어요!`
              : `프로그램을 공유해서 홍보해 보세요.\n해당 링크로 바로 판매할 수 있어요!`
          }
          handleActiveTab={handleTab}
          open={shareOpen}
          setOpen={setShareOpen}
          closeAlert={() => {
            setShareOpen(false);
            setTimeout(() => handleTab("coach")(), 350);
          }}
        />
        <ImagePickerDrawer
          inputRef={profilePicRef}
          onClickAlbum={selectProfilePic}
          onClickDefault={() => {
            setProfilePic("");
            closeImagePickerDrawer();
          }}
        />
        <ImageUploadAlert
          open={imageUploadAlert.open}
          setOpen={(state: boolean) => setImageUploadAlert({ ...imageUploadAlert, open: state })}
          header={imageUploadAlert.header}
          body={imageUploadAlert.body}
          onChange={imageUploadAlert.onChange}
        />
        <RequestUploadAlert
          open={requestUploadAlert.open}
          setOpen={(state: boolean) =>
            setRequestUploadAlert({ ...requestUploadAlert, open: state })
          }
          header={requestUploadAlert.header}
          body={requestUploadAlert.body}
          buttonLabel={requestUploadAlert.buttonLabel}
          onClick={requestUploadAlert.onClick}
          onCancel={requestUploadAlert.onCancel}
        />
      </div>
    </>
  );
};

const CoverImage = memo(({ coverImage, openImageUploadAlert }: any) => {
  return (
    <div className="cover-image">
      {coverImage ? (
        <>
          <label className="edit-icon" onClick={() => openImageUploadAlert("cover")}>
            <img src={CameraIcon} className="pick" />
          </label>

          <img
            src={typeof coverImage === "string" ? coverImage : URL.createObjectURL(coverImage)}
          />
        </>
      ) : (
        <>
          <label className="upload-button" onClick={() => openImageUploadAlert("cover")}>
            이미지 설정
          </label>
          <span>강사의 프로필 또는 이력을 어필할 수 있는 커버 이미지를 추가해주세요!</span>
        </>
      )}
    </div>
  );
});

const Overview = memo(({ name, profilePic, shortIntro, setShortIntro }: any) => {
  const { dispatch } = useContext(DispatchContext);

  const openImagePickerDrawer = () => {
    dispatch({
      type: "POP_UP_OPEN",
      id: "IMG_PICKER_DRAWER_POP",
      open: { imgPickerDrawerPop: true },
    });
  };

  return (
    <>
      <div className="overview">
        <div className="profile-picture" onClick={openImagePickerDrawer}>
          <img
            src={
              profilePic
                ? typeof profilePic === "object"
                  ? URL.createObjectURL(profilePic)
                  : profilePic
                : CoachDefault
            }
          />
          <img src={CameraIcon} className="pick" />
        </div>

        <div className="intro">
          <span>{name}</span>
          <input
            value={shortIntro}
            placeholder="강사의 한 줄 소개를 입력해주세요."
            maxLength={35}
            onChange={(e) => setShortIntro(e.target.value)}
          />
        </div>
      </div>

      <div className="tabs">
        <div>강사 소개</div>
        <div>프로그램</div>
        <div>리뷰</div>
      </div>
    </>
  );
});

const Greeting = memo(({ detailTitle, setDetailTitle, careerDesc, setCareerDesc }: any) => {
  return (
    <div className="greeting">
      <textarea
        className="introduction"
        placeholder="강사 소개 인사말을 45자 이내로 입력해주세요. 예) 안녕하세요! 켈리 코치입니다."
        maxLength={45}
        value={detailTitle}
        onChange={(e) => setDetailTitle(e.target.value)}
      />
      <textarea
        className="description"
        placeholder="임직원 관리의 강사님 한마디를 불러와, 미입력시 기본 인사말이 출력됩니다. (300자 제한)."
        maxLength={300}
        value={careerDesc}
        onChange={(e) => setCareerDesc(e.target.value)}
      />
    </div>
  );
});

const DetailImage = memo(({ detailImage, openImageUploadAlert }: any) => {
  return (
    <div className="detail-image">
      <div>
        {detailImage ? (
          <>
            <label className="edit-icon" onClick={() => openImageUploadAlert("detail")}>
              <img src={CameraIcon} className="pick" />
            </label>

            <img
              src={typeof detailImage === "string" ? detailImage : URL.createObjectURL(detailImage)}
            />
          </>
        ) : (
          <>
            <label className="upload-button" onClick={() => openImageUploadAlert("detail")}>
              이미지 설정
            </label>
            <span>강사 상세 이미지를 추가해주세요!</span>
          </>
        )}
      </div>
    </div>
  );
});

export default SellerProfileForm;
